/**
 * InjectionTokens für die Library.
 *
 * Tokens mit Default: Apps können diese überspringen, wenn das Feature nicht benötigt wird.
 * Tokens ohne Default (app-spezifisch): AUTH_CONTEXT, AUTH_PROVIDER, PROJECT_PROVIDER,
 *   DATA_PROVIDER, DB_CONNECTION_CONFIGS, DOCUMENT_HANDLER.
 * Component-Tokens (FILTER_COMPONENT etc.) haben keinen Default — Injections sind @Optional().
 */

import { InjectionToken, Type, inject } from '@angular/core';
import {
	IAuthContext,
	AuthProvider,
	ProjectProvider,
	DataProvider,
	FilterProvider,
	NavbarProvider,
	IEvaluationService,
	IValidationService,
	IOptionService,
	IHelpService,
	IHelpExtensionHandler,
	IConfirmService,
	IGeodataApiService
} from './interfaces';
import { BaseDocument, BaseProjectDocument } from './types';
import { DbConnectionConfig } from './db/db-connection-provider.interface';
import { NullFilterProvider } from './defaults/null-filter-provider';
import { SimpleNavbarProvider } from './defaults/simple-navbar-provider';
import { NullEvaluationService } from './defaults/null-evaluation-service';
import { NullValidationService } from './defaults/null-validation-service';
import { NullOptionService } from './defaults/null-option-service';
import { NullHelpService } from './defaults/null-help-service';
import { ConfirmService } from '../services/confirm.service';

// Auth Tokens
export const AUTH_CONTEXT = new InjectionToken<IAuthContext>('AuthContext');
export const AUTH_PROVIDER = new InjectionToken<AuthProvider>('AuthProvider');

// Provider Tokens
export const PROJECT_PROVIDER = new InjectionToken<ProjectProvider<BaseProjectDocument>>('ProjectProvider');
export const DATA_PROVIDER = new InjectionToken<DataProvider<BaseDocument>>('DataProvider');
export const FILTER_PROVIDER = new InjectionToken<FilterProvider>('FilterProvider', {
	providedIn: 'root',
	factory: () => new NullFilterProvider()
});
export const NAVBAR_PROVIDER = new InjectionToken<NavbarProvider>('NavbarProvider', {
	providedIn: 'root',
	factory: () => new SimpleNavbarProvider()
});

// DB Tokens
export const DB_CONNECTION_CONFIGS = new InjectionToken<DbConnectionConfig[]>('DbConnectionConfigs');

// Data Tokens
export { DOCUMENT_HANDLER } from './data/document-handler.interface';

// Structure Tokens (multi-provider — optional, leer wenn keine Structure-Services registriert)
export const STRUCTURE_PROVIDERS = new InjectionToken<any[]>('StructureProviders');

// Component Tokens
export const FILTER_COMPONENT = new InjectionToken<Type<any>>('FilterComponent');
export const SYNC_PROGRESS_COMPONENT = new InjectionToken<Type<any>>('SyncProgressComponent');
export const DOWNLOAD_PROGRESS_COMPONENT = new InjectionToken<Type<any>>('DownloadProgressComponent');
export const OFFLINE_DETAIL_COMPONENT = new InjectionToken<Type<any>>('OfflineDetailComponent');

// App-Service Tokens (für Services die nicht als Provider-Interface abstrahiert sind)
export const KEYCLOAK_ADMIN_SERVICE = new InjectionToken<any>('KeycloakAdminService', {
	providedIn: 'root',
	factory: () => null
});

// Map Tokens
export const WMS_CONFIG = new InjectionToken<any[]>('WmsConfig', {
	providedIn: 'root',
	factory: () => []
});

// Config Tokens
export const SENSITIVE_FIELDS = new InjectionToken<string[]>('SensitiveFields', {
	providedIn: 'root',
	factory: () => []
});

export interface ProjectMemberRoleConfig {
	/** Property auf dem Projekt-Dokument (z.B. 'bearbeiter') */
	field: string;
	/** UI-Label (z.B. 'Bearbeiter') */
	label: string;
	/** Plural-Label (z.B. 'Bearbeiter') */
	labelPlural: string;
	/** Text wenn keine Nutzer verfügbar */
	emptyText: string;
	/** Keycloak-Rolle zum Filtern (z.B. 'role:kartierer') */
	authRole: string;
	/** Bootstrap-Icon (z.B. 'bi-pencil') */
	icon: string;
	/** Nur sichtbar wenn Permission aktiv (z.B. 'aendern') */
	dependsOnPermission?: string;
}

export interface ProjectRolesConfig {
	/** Keycloak-Client-ID (z.B. 'bpweb') */
	keycloakClient: string;
	/** Konfigurierte Member-Rollen */
	memberRoles: ProjectMemberRoleConfig[];
	/** Rollen die Kommentare auflösen/löschen dürfen */
	commentReviewerRoles: string[];
	/** Rollen die Severity NICHT ändern dürfen */
	commentNoSeverityRoles: string[];
}

export const PROJECT_ROLES_CONFIG = new InjectionToken<ProjectRolesConfig>('ProjectRolesConfig', {
	providedIn: 'root',
	factory: () => ({ keycloakClient: '', memberRoles: [], commentReviewerRoles: [], commentNoSeverityRoles: [] })
});

// Structure Assignment Tokens (konfigurierbare Zuweisungs-Abschnitte im Projekt-Dialog)
export interface StructureAssignmentConfig {
	/** UI-Label des Accordion-Abschnitts (z.B. 'Forstbetriebe') */
	label: string;
	/** Property auf dem Projekt-Dokument (z.B. 'forstbetriebIds') */
	projectField: string;
	/** Service-Typ zum Laden der Entitäten (z.B. ForstbetriebeService) */
	serviceType: Type<any>;
	/** Anzeige-Property der Entität (z.B. 'name') */
	displayProperty: string;
	/** Optionales sekundäres Anzeige-Property (z.B. 'revier') */
	secondaryProperty?: string;
	/** Bootstrap-Icon (z.B. 'bi-tree') */
	icon?: string;
	/** Text wenn keine Entitäten vorhanden */
	emptyText?: string;
}

export const STRUCTURE_ASSIGNMENT_CONFIGS = new InjectionToken<StructureAssignmentConfig[]>(
	'StructureAssignmentConfigs', { providedIn: 'root', factory: () => [] }
);

// Offline-Dokumente (optionale zusätzliche Downloads beim Offline-Stellen)
export interface OfflineDocumentGroup {
	label: string;
	urls: string[];
}
export const OFFLINE_DOCUMENT_GROUPS = new InjectionToken<OfflineDocumentGroup[]>('OfflineDocumentGroups', {
	providedIn: 'root',
	factory: () => []
});

// Update-Service Tokens
export const APP_VERSION = new InjectionToken<string>('AppVersion', {
	providedIn: 'root',
	factory: () => '0.0.0'
});
export const CLEANUP_VERSIONS = new InjectionToken<string[]>('CleanupVersions', {
	providedIn: 'root',
	factory: () => []
});
export const VERSION_STORAGE_KEY = new InjectionToken<string>('VersionStorageKey', {
	providedIn: 'root',
	factory: () => 'wd_last_version'
});

// Migration Tokens
import { MigrationDefinition } from './types/migration.types';
export const CURRENT_DATA_VERSION = new InjectionToken<number>('CurrentDataVersion', {
	providedIn: 'root',
	factory: () => 0
});
export const BUILT_IN_MIGRATIONS = new InjectionToken<MigrationDefinition[]>('BuiltInMigrations', {
	providedIn: 'root',
	factory: () => []
});

// Project Extra Actions (app-spezifische Aktionen im Projektmenü)
export interface ProjectExtraAction {
	/** Button-Label */
	label: string;
	/** Bootstrap-Icon CSS-Klasse (z.B. 'bi-map') */
	icon: string;
	/** Callback bei Klick */
	action: () => void | Promise<void>;
}

export const PROJECT_EXTRA_ACTIONS = new InjectionToken<ProjectExtraAction[]>('ProjectExtraActions', {
	providedIn: 'root',
	factory: () => []
});

// Project New Dialog Extension Tokens (dynamische Accordion-Sektionen + Post-Create-Hooks)
export interface ProjectNewSection {
	/** Eindeutige ID */
	id: string;
	/** Accordion-Panel-Label */
	label: string;
	/** Component-Typ der im Accordion-Body gerendert wird */
	component: Type<any>;
	/** Bootstrap-Icon CSS-Klasse (z.B. 'bi-geo-alt') */
	icon?: string;
	/** Sortier-Priorität (niedriger = weiter oben) */
	order?: number;
	/** Ob die Sektion initial eingeklappt ist (Default: true) */
	collapsed?: boolean;
}

export const PROJECT_NEW_SECTIONS = new InjectionToken<ProjectNewSection[]>(
	'ProjectNewSections', { providedIn: 'root', factory: () => [] }
);

export interface ProjectPostCreateHandler {
	/** Wird nach dem Speichern eines neuen Projekts aufgerufen */
	onProjectCreated(project: any, sectionData: Map<string, any>): Promise<void>;
}

export const PROJECT_POST_CREATE_HANDLERS = new InjectionToken<ProjectPostCreateHandler[]>(
	'ProjectPostCreateHandlers', { providedIn: 'root', factory: () => [] }
);

// Project Deletion Handler (optionaler Hook für app-spezifische Löschlogik mit Progress)
export interface ProjectDeletionHandler {
	/** Wird statt der Standard-Löschung aufgerufen. Muss projectService.deleteProject() selbst aufrufen. */
	deleteProject(projectId: string): Promise<boolean>;
}

export const PROJECT_DELETION_HANDLER = new InjectionToken<ProjectDeletionHandler | null>(
	'ProjectDeletionHandler', { providedIn: 'root', factory: () => null }
);

// Project Entity Label (für UI-Texte — Default: "Projekt")
export interface ProjectEntityLabel {
	/** Nominativ Singular (z.B. "Projekt" / "Forstbetrieb") */
	name: string;
	/** Nominativ Plural (z.B. "Projekte" / "Forstbetriebe") */
	namePlural: string;
	/** "Neues Projekt" / "Neuer Forstbetrieb" */
	newLabel: string;
	/** Akkusativ mit Artikel: "das Projekt" / "den Forstbetrieb" */
	accusativeArticle: string;
	/** "Unbenanntes Projekt" / "Unbenannter Forstbetrieb" */
	unnamed: string;
	/** "keins" / "keiner" */
	noneLoaded: string;
}

export const PROJECT_ENTITY_LABEL = new InjectionToken<ProjectEntityLabel>('ProjectEntityLabel', {
	providedIn: 'root',
	factory: () => ({
		name: 'Projekt',
		namePlural: 'Projekte',
		newLabel: 'Neues Projekt',
		accusativeArticle: 'das Projekt',
		unnamed: 'Unbenanntes Projekt',
		noneLoaded: 'keins',
	})
});

// DGM Calibration API URL (optional — wenn gesetzt, wird DGM-Farbkalibrierung verfügbar)
export const DGM_API_URL = new InjectionToken<string | null>('DgmApiUrl', {
	providedIn: 'root',
	factory: () => null
});

// Route-Reuse Tokens
export const ROUTE_CACHE_TARGETS = new InjectionToken<string[]>('RouteCacheTargets', {
	providedIn: 'root',
	factory: () => []
});

// Filter-Konfiguration
export const FILTER_VALUES = new InjectionToken<any>('FilterValues', {
	providedIn: 'root',
	factory: () => ({})
});

// Service Tokens
export const EVALUATION_SERVICE = new InjectionToken<IEvaluationService>('EvaluationService', {
	providedIn: 'root',
	factory: () => new NullEvaluationService()
});
export const VALIDATION_SERVICE = new InjectionToken<IValidationService>('ValidationService', {
	providedIn: 'root',
	factory: () => new NullValidationService()
});
export const OPTION_SERVICE = new InjectionToken<IOptionService>('OptionService', {
	providedIn: 'root',
	factory: () => new NullOptionService()
});
export const HELP_SERVICE = new InjectionToken<IHelpService>('HelpService', {
	providedIn: 'root',
	factory: () => new NullHelpService()
});
export const HELP_EXTENSION_HANDLER = new InjectionToken<IHelpExtensionHandler | null>('HelpExtensionHandler', {
	providedIn: 'root',
	factory: () => null
});
export const CONFIRM_SERVICE = new InjectionToken<IConfirmService>('ConfirmService', {
	providedIn: 'root',
	factory: () => inject(ConfirmService)
});
export const GEODATA_API_SERVICE = new InjectionToken<IGeodataApiService | null>('GeodataApiService', {
	providedIn: 'root',
	factory: () => null
});
